import { useEffect, useState } from 'react';
import Modal from './Modal';
import PosPlanCard from './PosPlanCard';
import PosPlanModal from './PosPlanModal';
import { useConfig } from '../context/ConfigContext';
import { spinnerRing } from '../utils/bookingUtils';
import {
  subscribeSubscriptionPlans,
  saveSubscriptionPlan,
  deleteSubscriptionPlan,
} from '../utils/posSubscriptionUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * /admin -> Subscription Plans: what the Website Pal sells.
 *
 * Plans are shared by every client. A client's own subscription keeps a copy
 * of the plan it was set from, so editing or removing a plan here changes the
 * NEXT purchase and never a period already running.
 *
 * @param {object} props
 * @param {string} props.signedInEmail - recorded on the admin audit trail
 * @returns {JSX.Element}
 */
function PosSubscriptionPlans({ signedInEmail }) {
  const { config } = useConfig();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // undefined = dialog closed, null = adding, object = editing that plan.
  const [editing, setEditing] = useState(undefined);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => subscribeSubscriptionPlans(
    (list) => { setPlans(list); setLoading(false); setLoadError(null); },
    (error) => { setLoadError(error); setLoading(false); },
  ), []);

  /**
   * Writes the plan from the dialog, then closes it. A failure is rethrown so
   * the dialog stays open showing why.
   *
   * @param {object} draft
   * @returns {Promise<void>}
   */
  const handleSave = async (draft) => {
    try {
      await saveSubscriptionPlan(draft, signedInEmail);
      setEditing(undefined);
    } catch (error) {
      console.error('Saving plan failed:', error);
      throw new Error(describeWriteError(error, { what: 'this plan', path: 'posSubscriptionPlans' }));
    }
  };

  /** Removes the plan the confirmation is open on. @returns {Promise<void>} */
  const handleRemove = async () => {
    const plan = removing;
    setRemoving(null);
    setBusy(true);
    try {
      await deleteSubscriptionPlan(plan, signedInEmail);
    } catch (error) {
      console.error('Removing plan failed:', error);
      setModal({
        type: 'error',
        title: 'Could not remove',
        message: describeWriteError(error, { what: 'this plan', path: 'posSubscriptionPlans' }),
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="pa-loading">{spinnerRing('Loading plans')}</div>;
  }

  return (
    <div className="pa-screen ps-screen-wide">
      <header className="pa-screen-head">
        <div>
          <h1 className="pa-screen-title">Subscription Plans</h1>
          <p className="pa-screen-sub">
            What clients can avail, including free trials. Clients pick from the visible, non-trial
            plans when they subscribe from their till; trials are set by you on the client&rsquo;s
            Subscriptions tab.
          </p>
        </div>
        <button type="button" className="pa-btn pa-btn-primary" onClick={() => setEditing(null)}>Add plan</button>
      </header>

      {loadError ? (
        <div className="pa-empty">
          <h2 className="pa-empty-title">The plans could not be read</h2>
          <p className="pa-empty-body">{loadError.message}</p>
          <p className="pa-empty-body">
            Usually the database rules have not been deployed yet
            (<code className="pa-code">firebase deploy --only database</code>).
          </p>
        </div>
      ) : plans.length === 0 ? (
        <div className="pa-empty">
          <h2 className="pa-empty-title">No plans yet</h2>
          <p className="pa-empty-body">
            Start with a free trial and one paid plan — for example a 14-day trial and a monthly plan.
          </p>
          <button type="button" className="pa-btn pa-btn-primary" onClick={() => setEditing(null)}>Add plan</button>
        </div>
      ) : (
        <div className="ps-plan-grid">
          {plans.map((plan) => (
            <PosPlanCard key={plan.id} plan={plan} config={config} showAdminBadges>
              <button type="button" className="pa-btn pa-btn-quiet" onClick={() => setEditing(plan)} disabled={busy}>Edit</button>
              <button type="button" className="pa-btn pa-btn-quiet" onClick={() => setRemoving(plan)} disabled={busy}>Remove</button>
            </PosPlanCard>
          ))}
        </div>
      )}

      {busy && <div className="pa-saving">{spinnerRing('Saving')}</div>}

      <PosPlanModal
        isOpen={editing !== undefined}
        plan={editing || null}
        onSave={handleSave}
        onClose={() => setEditing(undefined)}
      />

      <Modal
        isOpen={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title="Remove plan?"
        message={removing
          ? `"${removing.name}" will no longer be offered. Clients already on it keep their current dates, and past applications keep its name.`
          : ''}
        type="error"
        showCancel
        confirmText="Remove plan"
        onConfirm={handleRemove}
      />

      <Modal
        isOpen={Boolean(modal)}
        onClose={() => setModal(null)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
      />
    </div>
  );
}

export default PosSubscriptionPlans;
